import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import classNames from 'classnames';
import {
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { UIPortalContext } from '../ui-theme/UITheme';
import styles from './Dialog.module.css';

type DialogSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'full';

export interface DialogProps
  extends Omit<
    HTMLAttributes<HTMLDialogElement>,
    'title' | 'onClose' | 'onClick' | 'onCancel'
  > {
  open: boolean;
  onClose: () => void;
  /** The modal body. */
  children: ReactNode;
  /** Omit for a modal that supplies its own chrome. */
  title?: ReactNode;
  /** Controls on the title's line, just left of the close button. */
  headerActions?: ReactNode;
  /** Actions. Laid out in a row, right-aligned; pass any node for more. */
  footer?: ReactNode;
  size?: DialogSize;
  placement?: 'center' | 'top';
  closeButton?: boolean;
  closeOnBackdropClick?: boolean;
  /** Focused on open, instead of the first tabbable element. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** On the dialog itself. */
  className?: string;
  /** On the body. The escape hatch for modals whose content owns its layout. */
  bodyClassName?: string;
  'data-testid'?: string;
}

/**
 * Dialog dialog on the native `<dialog>` element, which already is the panel,
 * the backdrop (`::backdrop`) and the positioner (the top layer) — so unlike
 * every headless library's dialog, this needs no Root, Portal, Overlay or Panel
 * part, and no z-index. It also brings the focus trap and Escape for free.
 *
 * Deliberately one component rather than a compound set: measured across the
 * 63 Chakra modal sites in this app, nothing nests a modal in a modal and
 * nothing uses render-prop children, so there is no state for sub-components to
 * share. `title` and `footer` take nodes, which covers the rich headers and the
 * complex footers too; a modal wanting different chrome omits both and renders
 * its own markup as `children`.
 */
export const Dialog = ({
  open,
  onClose,
  children,
  title,
  headerActions,
  footer,
  size = 'md',
  placement = 'center',
  closeButton = true,
  closeOnBackdropClick = true,
  initialFocusRef,
  className,
  bodyClassName,
  ...rest
}: DialogProps) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  // Also held in state so it can be handed to UIPortalContext: a ref alone
  // would not re-render the consumers once the element exists.
  const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null);
  const attachDialog = useCallback((node: HTMLDialogElement | null) => {
    dialogRef.current = node;
    setDialogEl(node);
    // The element only exists while open, so show it the moment it attaches —
    // before effects run, so nothing paints in the un-modal state first.
    if (node && !node.open) node.showModal();
  }, []);
  const titleId = useId();
  const heading =
    title === undefined ? null : (
      <h2 id={titleId} className={styles.title}>
        {title}
      </h2>
    );

  useEffect(() => {
    if (open) {
      initialFocusRef?.current?.focus();
    }
  }, [open, initialFocusRef]);

  // The native default is for the dialog to close itself; let React own the
  // open state instead so a controlled `open` cannot drift out of sync.
  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    onClose();
  };

  // A `click` fires on the nearest common ancestor of the mousedown and mouseup
  // targets, so a drag that starts on the content and ends over the dialog box
  // — selecting a title and releasing on the chart, say — reports the dialog as
  // its target. Requiring the press to have STARTED on the backdrop keeps text
  // selection from dismissing the modal.
  const pressedOnBackdrop = useRef(false);

  const handleMouseDown = (event: MouseEvent<HTMLDialogElement>) => {
    pressedOnBackdrop.current = event.target === dialogRef.current;
  };

  const handleClick = (event: MouseEvent<HTMLDialogElement>) => {
    const onBackdrop =
      event.target === dialogRef.current && pressedOnBackdrop.current;
    pressedOnBackdrop.current = false;
    if (closeOnBackdropClick && onBackdrop) {
      onClose();
    }
  };

  // Unmount rather than keep a closed <dialog> around: Chakra's modal did the
  // same, and a lingering empty element makes `not.toBeInTheDocument()` assertions
  // impossible and every `getBy*` ambiguous once two instances are mounted.
  if (!open) return null;

  return (
    <dialog
      ref={attachDialog}
      className={classNames(styles.dialog, className)}
      data-size={size}
      data-placement={placement}
      aria-labelledby={title === undefined ? undefined : titleId}
      onCancel={handleCancel}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      {...rest}
    >
      {/* `showModal()` puts the dialog in the top layer, above the entire normal
          stacking context, and makes the rest of the document inert. Anything
          portaled to document.body therefore paints UNDER the backdrop and
          cannot be clicked — at any z-index. So offer the dialog itself as the
          portal target, keeping Select menus, tooltips and popovers inside the
          top-layer subtree where they still work. */}
      <UIPortalContext.Provider value={dialogEl ?? document.body}>
        {headerActions === undefined ? (
          heading
        ) : (
          <div className={styles.headerRow}>
            {heading}
            <div className={styles.headerActions}>{headerActions}</div>
          </div>
        )}
        {closeButton ? (
          <button
            type="button"
            aria-label="Close"
            className={styles.closeButton}
            onClick={onClose}
          >
            <IconCrossLarge size={16} />
          </button>
        ) : null}
        <div className={classNames(styles.body, bodyClassName)}>{children}</div>
        {footer === undefined ? null : (
          <footer className={styles.footer}>{footer}</footer>
        )}
      </UIPortalContext.Provider>
    </dialog>
  );
};
